/* ══════════════════════════════════════════════════════════════════════════
   Monmouth Cards — homepage Mystery Packs tier carousel
   assets/monmouth-mystery-carousel.js        (sections/monmouth-mystery-packs.liquid)

   A seamless, looping carousel for the six tier cards.

   HOW THE LOOP WORKS
   The six real cards (the theme blocks) are the only editable content. At start-up this script makes whole extra
   sets of copies (clones) of them before and after the real set. Every set is identical and a set is exactly
   N * step pixels wide, so showing the strip shifted by (offset mod setWidth) looks the same whatever the offset:
   there is no end, no gap and no jump. Clones are aria-hidden, skipped by Tab (tabindex -1) and lose their editor id. They stay real links (an `inert`
   attribute would also stop clicks on them, and clones are often the visible cards after the strip wraps).

   MOTION: only transform (translate3d) inside requestAnimationFrame; the loop runs only while something is moving
   and stops when the section is off-screen or the tab is hidden. No auto-play. prefers-reduced-motion: no
   gliding or easing (arrows jump one card; drag still works and settles instantly).

   INPUT
     mouse hover  edge of the strip -> cards glide the other way (speed grows with closeness to the edge, eased)
     arrows       one card per click, eased
     drag / swipe pointer events; swipe has momentum and settles on a card; a drag never counts as a click
     keyboard     Left / Right arrow while focus is inside the carousel; Tab never gets trapped
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var TAU = 0.22;            // seconds: how quickly hover velocity eases toward its target
  var DEADZONE = 0.28;       // middle share of the strip where the hover glide rests
  var DRAG_START = 6;        // px before a press becomes a drag (so plain clicks still work)

  function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

  function setup(root) {
    if (!root || root.__mcCarousel) return;
    var viewport = root.querySelector('[data-mc-viewport]');
    var track = root.querySelector('[data-mc-track]');
    var prevBtn = root.querySelector('[data-mc-prev]');
    var nextBtn = root.querySelector('[data-mc-next]');
    if (!viewport || !track) return;

    var reals = Array.prototype.slice.call(track.children);
    var N = reals.length;
    if (N < 2) return;
    root.__mcCarousel = true;

    var maxSpeed = parseFloat(root.getAttribute('data-speed')) || 240;
    var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

    var step = 0, setW = 0, viewW = 0, k = 1;
    var offset = 0;           // unbounded; shown modulo setW
    var vel = 0, targetVel = 0;
    var tween = null;
    var dragging = false, drag = null, suppressClick = false;
    var raf = 0, last = 0;
    var onScreen = true;

    /* ── clones ── */
    var clones = [];
    function clearClones() { clones.forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); }); clones = []; }
    function makeClone(src) {
      var c = src.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('tabindex', '-1');
      c.removeAttribute('data-shopify-editor-block');
      c.setAttribute('data-mc-clone', '1');
      Array.prototype.forEach.call(c.querySelectorAll('a,button,[tabindex]'), function (n) { n.setAttribute('tabindex', '-1'); });
      return c;
    }
    function buildClones() {
      clearClones();
      var before = document.createDocumentFragment(), after = document.createDocumentFragment();
      for (var i = 0; i < k; i++) {
        reals.forEach(function (r) {
          var b = makeClone(r), a = makeClone(r);
          before.appendChild(b); after.appendChild(a); clones.push(b, a);
        });
      }
      track.insertBefore(before, track.firstChild);
      track.appendChild(after);
    }

    /* ── measure ── */
    function measure() {
      var cs = window.getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap) || 0;
      var w = reals[0].getBoundingClientRect().width;
      var newStep = w + gap;
      viewW = viewport.clientWidth;
      if (!newStep || !viewW) return false;
      if (step) offset = offset / step * newStep;   // keep the same card in place when the width changes
      step = newStep;
      setW = N * step;
      var needK = Math.max(1, Math.ceil(viewW / setW));
      if (needK !== k || !clones.length) { k = needK; buildClones(); }
      apply();
      return true;
    }

    /* ── drawing ── */
    function mod(a, m) { return ((a % m) + m) % m; }
    function apply() {
      if (!setW) return;
      var shown = mod(offset, setW);
      track.style.transform = 'translate3d(' + (-(k * setW + shown)).toFixed(2) + 'px,0,0)';
      var r = mod(offset, step);
      root.classList.toggle('is-unaligned', r > 1.5 && step - r > 1.5);
    }

    /* ── motion loop ── */
    function moving() { return !!tween || dragging || Math.abs(vel) > 0.5 || targetVel !== 0; }
    function frame(t) {
      var dt = Math.min(0.05, Math.max(0.001, (t - last) / 1000));
      last = t;
      if (tween) {
        var p = (t - tween.t0) / tween.dur;
        if (p >= 1) { offset = tween.to; tween = null; vel = 0; }
        else { offset = tween.from + (tween.to - tween.from) * tween.fn(p); }
      } else if (!dragging) {
        vel += (targetVel - vel) * (1 - Math.exp(-dt / TAU));
        if (targetVel === 0 && Math.abs(vel) < 0.5) vel = 0;
        offset += vel * dt;
      }
      apply();
      root.classList.toggle('is-moving', moving());
      if (moving() && onScreen && !document.hidden) { raf = requestAnimationFrame(frame); }
      else { raf = 0; root.classList.remove('is-moving'); }
    }
    function kick() {
      if (raf || !onScreen || document.hidden) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
    function stopAll() {
      targetVel = 0; vel = 0; tween = null;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      root.classList.remove('is-moving');
    }

    /* ── move one card (arrows, keys) ── */
    function go(dir) {
      var to;
      if (tween) { to = tween.to + dir * step; }
      else {
        var cur = offset / step;
        to = (dir > 0 ? Math.floor(cur + 0.002) + 1 : Math.ceil(cur - 0.002) - 1) * step;
      }
      if (reduceMq.matches) { tween = null; vel = 0; targetVel = 0; offset = to; apply(); return; }
      var from = offset;
      tween = { from: from, to: to, t0: performance.now(), dur: 480, fn: ease };
      vel = 0;
      kick();
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(1); });

    /* ── keyboard ── */
    root.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });

    /* a focused real card must be on screen (the browser would otherwise scroll the clipped viewport) */
    viewport.addEventListener('scroll', function () { viewport.scrollLeft = 0; });
    root.addEventListener('focusin', function (e) {
      var card = e.target && e.target.closest ? e.target.closest('.mc-mystery__tier') : null;
      if (!card || card.hasAttribute('data-mc-clone')) return;
      var idx = reals.indexOf(card);
      if (idx < 0) return;
      var cardW = reals[0].getBoundingClientRect().width;
      var left = (k * N + idx) * step - (k * setW + mod(offset, setW));   // card's x inside the viewport now
      left = mod(left + setW / 2, setW) - setW / 2;                         // nearest copy
      var delta = 0;
      if (left < 0) delta = left;
      else if (left + cardW > viewW) delta = left + cardW - viewW;
      if (!delta) return;
      if (reduceMq.matches) { offset += delta; apply(); return; }
      tween = { from: offset, to: offset + delta, t0: performance.now(), dur: 380, fn: ease };
      vel = 0; kick();
    });

    /* ── hover glide (mouse only) ── */
    function hover(e) {
      if (e.pointerType !== 'mouse' || dragging || reduceMq.matches || !viewW) return;
      var rect = viewport.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 2 - 1;      // -1 (left edge) .. 1 (right edge)
      var a = Math.abs(x);
      var t = a <= DEADZONE ? 0 : Math.min(1, (a - DEADZONE) / (1 - DEADZONE));
      t = t * t * (3 - 2 * t);                                      // smoothstep: gentle start, gentle top speed
      targetVel = (x < 0 ? -1 : 1) * maxSpeed * t;
      if (!tween) kick();
    }
    root.addEventListener('pointermove', function (e) {
      if (drag && e.pointerId === drag.id) { dragMove(e); return; }
      hover(e);
    });
    root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { targetVel = 0; if (moving()) kick(); } });

    /* ── drag / swipe ── */
    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, off0: offset, moved: false, s: [{ t: performance.now(), x: e.clientX }] };
      suppressClick = false;
    });
    function dragMove(e) {
      var dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_START || Math.abs(dx) < Math.abs(dy)) return;
        drag.moved = true; dragging = true; tween = null; vel = 0; targetVel = 0;
        try { viewport.setPointerCapture(drag.id); } catch (x) { /* no-op */ }
        root.classList.add('is-dragging');
      }
      offset = drag.off0 - dx;
      var now = performance.now();
      drag.s.push({ t: now, x: e.clientX });
      while (drag.s.length > 2 && now - drag.s[0].t > 120) drag.s.shift();
      apply();
      root.classList.toggle('is-moving', true);
    }
    function dragEnd(e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var d = drag; drag = null;
      if (!d.moved) return;
      dragging = false;
      root.classList.remove('is-dragging');
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 60);
      var s = d.s, v = 0;
      if (s.length > 1) {
        var a = s[0], b = s[s.length - 1];
        if (b.t > a.t) v = -(b.x - a.x) / ((b.t - a.t) / 1000);           // px/s, + = cards moving left
      }
      var projected = offset + v * 0.26;
      var to = Math.round(projected / step) * step;
      var lim = 3 * step;
      to = Math.max(offset - lim, Math.min(offset + lim, to));
      if (reduceMq.matches) { offset = Math.round(offset / step) * step; apply(); return; }
      var dist = Math.abs(to - offset);
      tween = { from: offset, to: to, t0: performance.now(), dur: Math.max(320, Math.min(900, 280 + dist * 0.9)), fn: easeOut };
      vel = 0; kick();
    }
    viewport.addEventListener('pointerup', dragEnd);
    viewport.addEventListener('pointercancel', dragEnd);
    viewport.addEventListener('lostpointercapture', dragEnd);
    // a drag must not open a card; a plain click must
    viewport.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ── stop when hidden / off-screen ── */
    document.addEventListener('visibilitychange', function () { if (document.hidden) stopAll(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (!onScreen) stopAll();
      }).observe(root);
    }
    if (reduceMq.addEventListener) reduceMq.addEventListener('change', function () { if (reduceMq.matches) stopAll(); });

    /* ── size changes ── */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { measure(); });
      ro.observe(viewport);
    } else { window.addEventListener('resize', measure); }

    if (!measure()) {
      // not laid out yet (hidden tab / editor): try again when it becomes visible
      var tries = 0;
      var iv = setInterval(function () { if (measure() || ++tries > 20) clearInterval(iv); }, 250);
    }
    root.classList.add('is-ready');
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mc-carousel]'), setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
